/**
 * src/v2/config.ts
 * V2 contract configuration and country code utilities.
 *
 * All on-chain operations use u32 ISO 3166-1 numeric country codes.
 * resolveCountry() converts alpha-2 strings to numeric.
 * numericToAlpha2() converts numeric to alpha-2 for display.
 */

import { V2ContractAddresses } from './types';

// ── Backend API base URL ──────────────────────────────────────────────────────

const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
export const API_BASE = isDev
  ? `${window.location.protocol}//${window.location.hostname}:8080`
  : `${window.location.protocol}//${window.location.host}`;

export const V2_API = `${API_BASE}/api/v2`;

// ── V2 Contract addresses (loaded from backend /api/v2/contracts) ─────────────

let _addresses: V2ContractAddresses | null = null;

export async function getV2Contracts(): Promise<V2ContractAddresses> {
  if (_addresses) return _addresses;

  // Try env vars first (fast path for dev)
  const envAddresses: Partial<V2ContractAddresses> = {
    vault:      (process.env as any).REACT_APP_V2_VAULT_ID,
    compliance: (process.env as any).REACT_APP_V2_COMPLIANCE_ID,
    session:    (process.env as any).REACT_APP_V2_SESSION_ID,
    zkVerifier: (process.env as any).REACT_APP_V2_ZK_VERIFIER_ID,
    core:       (process.env as any).REACT_APP_V2_CORE_ID,
  };

  if (Object.values(envAddresses).every(Boolean)) {
    _addresses = envAddresses as V2ContractAddresses;
    return _addresses;
  }

  // Fetch from backend
  const resp = await fetch(`${V2_API}/contracts`);
  if (!resp.ok) throw new Error(`Failed to fetch V2 contract addresses: ${resp.status}`);
  _addresses = await resp.json();
  return _addresses!;
}

/** Invalidate cached addresses (call after contract upgrade) */
export function clearV2ContractCache() {
  _addresses = null;
}

// ── Country code utilities ────────────────────────────────────────────────────

export const ALPHA2_TO_NUMERIC: Record<string, number> = {
  'US':840,'GB':826,'CA':124,'AU':36,'DE':276,'FR':250,'JP':392,'CN':156,
  'IT':380,'ES':724,'NL':528,'BE':56,'GR':300,'PT':620,'SE':752,'NO':578,
  'DK':208,'FI':246,'PL':616,'CZ':203,'HU':348,'RO':642,'BG':100,'HR':191,
  'SK':703,'SI':705,'EE':233,'LV':428,'LT':440,'IE':372,'IS':352,'CH':756,
  'AT':40,'LU':442,'MT':470,'CY':196,'MX':484,'BR':76,'AR':32,'CL':152,
  'CO':170,'PE':604,'VE':862,'EC':218,'BO':68,'PY':600,'UY':858,'IN':356,
  'PK':586,'BD':50,'ID':360,'PH':608,'VN':704,'TH':764,'MY':458,'SG':702,
  'KR':410,'TW':158,'HK':344,'NZ':554,'ZA':710,'EG':818,'NG':566,'KE':404,
  'ET':231,'GH':288,'TZ':834,'UG':800,'MA':504,'DZ':12,'TN':788,'LY':434,
  'SD':729,'AO':24,'MZ':508,'ZM':894,'ZW':716,'BW':72,'NA':516,'MG':450,
  'RU':643,'UA':804,'BY':112,'KZ':398,'UZ':860,'TM':795,'TJ':762,'KG':417,
  'AF':4,'IR':364,'IQ':368,'SA':682,'AE':784,'IL':376,'JO':400,'KW':414,
  'LB':422,'SY':760,'TR':792,'GE':268,'AM':51,'AZ':31,'AL':8,'MK':807,
  'RS':688,'BA':70,'ME':499,'XK':383,'MD':498,
};

export const NUMERIC_TO_ALPHA2: Record<number, string> = Object.fromEntries(
  Object.entries(ALPHA2_TO_NUMERIC).map(([k, v]) => [v, k])
);

/**
 * Resolve a country input to a u32 numeric code.
 * Accepts: number, numeric string ("840"), or ISO alpha-2 ("US").
 */
export function resolveCountry(input: string | number): number {
  if (typeof input === 'number') return input;
  const parsed = parseInt(input, 10);
  if (!isNaN(parsed) && String(parsed) === input) return parsed;
  const upper = input.toUpperCase();
  const code = ALPHA2_TO_NUMERIC[upper];
  if (code !== undefined) return code;
  throw new Error(`Unknown country: ${input}`);
}

/** Convert numeric country code to alpha-2 for display. */
export function numericToAlpha2(code: number): string | null {
  return NUMERIC_TO_ALPHA2[code] ?? null;
}

// ── V2 Error code → message ────────────────────────────────────────────────────

export const V2_ERROR_MESSAGES: Record<number, string> = {
  1:  'Not initialized', 2: 'Unauthorized', 3: 'Country not registered',
  4:  'Country already registered', 5: 'Invalid numeric country code',
  6:  'Contract registry not set',
  100:'Country vault disabled', 101:'Insufficient balance',
  102:'Balance overflow', 103:'Balance underflow',
  104:'Invalid amount', 105:'Transfer to same country',
  106:'Transfer not allowed', 107:'Compliance check failed',
  108:'Daily limit exceeded', 109:'Monthly limit exceeded',
  110:'Transaction amount too small', 111:'Transaction amount too large',
  112:'WebAuthn signature required', 113:'WebAuthn signature invalid',
  200:'Policy not found for country', 201:'Country is restricted',
  202:'Sanctions block', 203:'KYC required',
  204:'KYC rejected', 205:'User account restricted',
  206:'Deposits disabled for this country',
  207:'Withdrawals disabled for this country',
  208:'Transfers in disabled for this country',
  209:'Transfers out disabled for this country',
  300:'Session not found', 301:'Session not in waiting state',
  302:'Session not active', 303:'Session is full',
  304:'Already joined this session', 305:'Not a member of required trust group',
  306:'ZK proof required for this session',
  307:'ZK proof invalid', 308:'Event already anchored',
  400:'VK not set', 401:'VK not found', 402:'Proof already used (replay)',
  403:'Proof too old', 404:'Proof malformed',
  405:'Public inputs mismatch', 406:'Pairing verification failed',
  407:'Unsupported proof type', 408:'Unsupported curve',
  409:'Context mismatch', 410:'Batch size too large',
};

export function decodeV2Error(code: number): string {
  return V2_ERROR_MESSAGES[code] ?? `Contract error ${code}`;
}
